import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import CTASection  from '../components/CTASection';
import Reveal      from '../components/Reveal';

/* -------------------------------------------------------
   ICON COMPONENTS (inline SVG, no dependency)
   ------------------------------------------------------- */
const IconGovernance = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="1"/>
    <path d="M7 11V7a5 5 0 0110 0v4"/>
    <circle cx="12" cy="16" r="1"/>
  </svg>
);
const IconServices = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="3" width="6" height="6" rx="1"/>
    <rect x="16" y="3" width="6" height="6" rx="1"/>
    <rect x="9" y="14" width="6" height="7" rx="1"/>
    <path d="M5 9v3M19 9v3M5 12h14M12 12v2"/>
  </svg>
);
const IconCapital = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6"/>
  </svg>
);
const IconRisk = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4"/>
  </svg>
);

/* -------------------------------------------------------
   VALUE CREATION PILLARS
   ------------------------------------------------------- */
const PILLARS = [
  {
    icon:  <IconGovernance />,
    title: 'Governance Maturity',
    body:  'Structured oversight frameworks govern every brand in the portfolio. Clear policies, documented controls, and defined escalation paths ensure decisions are traceable, consistent, and defensible at every level of the organization.',
  },
  {
    icon:  <IconServices />,
    title: 'Shared Services Infrastructure',
    body:  'Finance, legal, technology, marketing operations, and analytics functions are centralized across the portfolio. This architecture eliminates redundancy, accelerates decision-making, and compounds institutional knowledge over time.',
  },
  {
    icon:  <IconCapital />,
    title: 'Capital Discipline',
    body:  'Resources are deployed where they generate the highest return on effort and investment. Allocation decisions are data-informed, reviewed on a regular cadence, and tied directly to performance metrics at the brand level.',
  },
  {
    icon:  <IconRisk />,
    title: 'Risk Management',
    body:  'Proactive identification and management of operational, legal, reputational, and market risk is embedded in how we operate. Risk registers, vendor assessments, and compliance protocols protect the portfolio and its stakeholders.',
  },
];

/* -------------------------------------------------------
   PORTFOLIO SNAPSHOT (brief)
   ------------------------------------------------------- */
const BRANDS_SNAP = [
  { name: 'DriveTalkAI',   category: 'AI Communications',        domain: 'driventalkai.com' },
  { name: 'AddyMay',       category: 'Health & Beauty Commerce', domain: 'AddyMay.com'      },
  { name: 'Driven2Win',    category: 'Performance Marketing',    domain: 'Driven2win.com'   },
  { name: 'PIC and SAV',   category: 'E-Commerce & Marketplace', domain: 'PICandSAV.com'    },
];

/* -------------------------------------------------------
   HOME PAGE
   ------------------------------------------------------- */
export default function Home() {
  return (
    <>
      <Helmet>
        <title>Driven 2 LLC — Disciplined Ownership. Scalable Operations.</title>
        <meta
          name="description"
          content="Driven 2 LLC is a disciplined holding company that builds, owns, and operates a portfolio of digital brands through centralized governance, shared services, and long-term stewardship."
        />
        <meta property="og:title" content="Driven 2 LLC — Disciplined Ownership. Scalable Operations." />
        <meta property="og:description" content="A disciplined holding company and operator of a portfolio of digital brands. Centralized governance, shared infrastructure, and long-term value creation." />
        <meta property="og:url" content="https://www.driven2.net/" />
        <link rel="canonical" href="https://www.driven2.net/" />
      </Helmet>

      {/*
        ═══════════════════════════════════════════
        HERO
        PLACEHOLDER DIRECTION:
        "Executive-level corporate hero — modern
        city skyline at dusk with glass-tower
        reflections, subtle blue-gold tones,
        disciplined investment-firm atmosphere.
        Dark overlay for light text legibility."

        TO REPLACE: <HeroSection bgImage="/images/hero-home.jpg" ... />
        ═══════════════════════════════════════════
      */}
      <HeroSection
        eyebrow="Driven 2 LLC · Florida"
        title={"Disciplined Ownership.\nScalable Operations."}
        subtitle="We build, acquire, and operate a portfolio of digital brands through centralized governance, shared infrastructure, and a long-term ownership discipline that prioritizes sustainable value over short-term returns."
        ctaPrimary={{ label: 'Explore Our Portfolio', to: '/brands' }}
        ctaSecondary={{ label: 'View Operating Model', to: '/operating-model' }}
        size="full"
        bgImage="https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1920&q=80"
      />

      {/* ═══════════════════════════════════════
          WHAT WE DO
          ═══════════════════════════════════════ */}
      <section className="section section--alt">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'start' }}>

            <Reveal>
              <div className="section-intro">
                <div className="eyebrow">What We Do</div>
                <h2 className="d-lg t-navy">
                  A Holding Company Built for the Long Term
                </h2>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <p className="lead mb-8">
                Driven 2 LLC is a private holding company based in Florida. We acquire, build, and steward a portfolio of digital brands —
                providing each with the governance, infrastructure, and operational
                discipline required to perform consistently across market conditions.
              </p>
              <p className="lead">
                Our approach is defined by long-horizon thinking, operational
                transparency, and the belief that well-governed businesses with strong
                fundamentals compound value over time. We are not a venture firm. We
                are an owner-operator — with the systems and standards to prove it.
              </p>
              <div style={{ marginTop: 'var(--sp-10)', display: 'flex', gap: 'var(--sp-12)', flexWrap: 'wrap' }}>
                {[
                  { num: 'Long-Term',     label: 'Ownership Horizon'   },
                  { num: 'Multi-Channel', label: 'Market Reach'        },
                  { num: 'FL',            label: 'Operating Base'      },
                ].map(({ num, label }) => (
                  <div key={label} className="stat">
                    <div className="stat__num">{num}</div>
                    <div className="stat__label">{label}</div>
                  </div>
                ))}
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          THREE CORE ACTIVITIES
          ═══════════════════════════════════════ */}
      <section className="section section--dark">
        <div className="container">
          <Reveal>
            <div className="section-intro section-intro--center">
              <div className="eyebrow eyebrow--center eyebrow--light">How We Create Value</div>
              <h2 className="d-lg t-white">Acquire. Operate. Scale.</h2>
              <p className="lead lead--white" style={{ maxWidth: '600px', margin: '0 auto' }}>
                Three disciplined activities form the core of our operating mandate.
                Each is governed by clear process, measured by defined metrics, and
                continuously refined.
              </p>
            </div>
          </Reveal>

          <div className="grid-3" style={{ marginTop: 'var(--sp-12)' }}>
            {[
              {
                num:   '01',
                title: 'Acquire',
                body:  'We identify digital brands with durable fundamentals, addressable markets, and operational improvement potential. Our acquisition thesis prioritizes businesses where structured governance and shared services can meaningfully accelerate performance.',
              },
              {
                num:   '02',
                title: 'Operate',
                body:  'Portfolio brands are operated with institutional discipline — standardized reporting, defined KPIs, formalized risk management, and centralized support functions that reduce overhead while elevating execution quality.',
              },
              {
                num:   '03',
                title: 'Scale',
                body:  'We build brands for the long term. Our shared infrastructure scales with the portfolio, compounding institutional knowledge, operational efficiency, and brand equity in ways that standalone operations cannot replicate.',
              },
            ].map(({ num, title, body }, i) => (
              <Reveal key={num} delay={i * 0.1}>
                <div className="card card--dark" style={{ height: '100%' }}>
                  <div className="card__num">{num}</div>
                  <h3 className="card__title">{title}</h3>
                  <p className="card__body">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          VALUE CREATION LEVERS
          ═══════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-intro">
              <div className="eyebrow">Value Creation Levers</div>
              <h2 className="d-lg t-navy" style={{ maxWidth: '580px' }}>
                The Operational Foundation Beneath Every Brand
              </h2>
            </div>
          </Reveal>

          <div className="grid-2" style={{ gap: '0', borderTop: '1px solid var(--gray-200)' }}>
            {PILLARS.map(({ icon, title, body }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div
                  className="pillar"
                  style={{
                    padding: 'var(--sp-10) var(--sp-8)',
                    borderRight: i % 2 === 0 ? '1px solid var(--gray-200)' : 'none',
                    borderBottom: '1px solid var(--gray-200)',
                  }}
                >
                  <div className="pillar__icon" style={{ color: 'var(--gold-500)' }}>
                    {icon}
                  </div>
                  <h3 className="pillar__title">{title}</h3>
                  <p className="pillar__body">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          PORTFOLIO SNAPSHOT
          ═══════════════════════════════════════ */}
      <section className="section section--alt">
        <div className="container">
          <Reveal>
            <div className="section-intro" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 'var(--sp-6)' }}>
              <div>
                <div className="eyebrow">Portfolio</div>
                <h2 className="d-lg t-navy mb-0">Our Digital Brand Portfolio</h2>
              </div>
              <Link to="/brands" className="btn btn-outline-navy hide-mobile">
                View All Brands
              </Link>
            </div>
          </Reveal>

          <div className="grid-4">
            {BRANDS_SNAP.map(({ name, category, domain }, i) => (
              <Reveal key={name} delay={i * 0.09}>
                <div
                  className="card"
                  style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', height: '100%' }}
                >
                  <div className="card__tag">{category}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 500, color: 'var(--navy-800)', marginBottom: 'var(--sp-1)' }}>
                    {name}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', letterSpacing: '0.05em', marginTop: 'auto', paddingTop: 'var(--sp-4)' }}>
                    {domain}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div style={{ marginTop: 'var(--sp-8)', textAlign: 'center' }} className="hide-desktop">
            <Link to="/brands" className="btn btn-outline-navy">
              View Full Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          GOVERNANCE POSTURE
          ═══════════════════════════════════════ */}
      <section className="section section--deeper">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'center' }}>

            <Reveal>
              <div className="eyebrow eyebrow--light">Governance &amp; Risk</div>
              <h2 className="d-lg t-white mb-8">
                Oversight You Can Rely On
              </h2>
              <p className="lead lead--white mb-6">
                Our governance posture is designed not only for today's operational
                demands, but for the scrutiny that comes with growth — whether that
                means institutional partnerships, acquisition conversations, or capital
                events.
              </p>
              <p className="lead lead--white">
                Documented controls, standardized reporting, and a consistent risk
                management cadence mean our portfolio is always audit-ready,
                partner-ready, and performance-ready.
              </p>
              <div style={{ marginTop: 'var(--sp-10)' }}>
                <Link to="/operating-model" className="btn btn-ghost-gold">
                  Explore Operating Model
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-8)' }}>
                {[
                  ['Reporting',    'Regular-cadence performance reporting across all portfolio brands.'],
                  ['Controls',     'Documented operational and financial controls at brand and holding level.'],
                  ['Compliance',   'Active legal, data privacy, and regulatory compliance management.'],
                  ['Integration',  'Structured onboarding playbooks for new brand acquisitions and integrations.'],
                ].map(([title, desc]) => (
                  <div key={title} style={{ borderLeft: '1px solid rgba(184,150,90,0.35)', paddingLeft: 'var(--sp-5)' }}>
                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold-400)', marginBottom: 'var(--sp-2)' }}>
                      {title}
                    </div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.75 }}>
                      {desc}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          CTA
          ═══════════════════════════════════════ */}
      <CTASection
        eyebrow="Engage With Driven 2"
        headline="Ready to Explore a Partnership?"
        subtext="Whether you are a prospective brand partner, investor, or acquisition counterparty, we welcome substantive conversations with serious counterparts."
        primaryCTA={{ label: 'Contact Driven 2', to: '/contact' }}
        secondaryCTA={{ label: 'Learn About the Company', to: '/about' }}
        variant="dark"
      />
    </>
  );
}
